import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft, Save, Edit, User, FileText, Ban, Eye, Zap, Shield, Crown,
  Coins, ShoppingCart, TrendingDown, Wallet, Clock, Globe, ChevronLeft,
  ChevronRight, MapPin, Mail, Phone, Settings,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminPurchaseHistoryPanel from "../components/admin/AdminPurchaseHistoryPanel";
import UserEmailPicker from "../components/admin/UserEmailPicker";
import UserGuardianCard from "../components/admin/UserGuardianCard";

// ==================== SHARED HELPERS ====================
const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " +
         d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// ==================== LOGIN HISTORY TAB (paginated 25/page) ====================
function LoginHistoryTab({ userId }) {
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 25;

  useEffect(() => {
    const fetchLogins = async () => {
      try {
        const res = await authAxios.get(`/account/admin/login-history/${userId}`);
        setLogins(res.data.logins || []);
      } catch (e) { console.error("Login history fetch error:", e); }
      finally { setLoading(false); }
    };
    fetchLogins();
  }, [userId]);

  const totalPages = Math.ceil(logins.length / perPage);
  const pageLogins = logins.slice((page - 1) * perPage, page * perPage);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-divine-gold/30 border-t-divine-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="login-history-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" /> Login History
          <span className="text-xs text-gray-500 font-normal ml-2">({logins.length} total, max 100)</span>
        </CardTitle>
        <CardDescription className="text-gray-400">
          Partial IP and country for each login &mdash; privacy-compliant audit trail
        </CardDescription>
      </CardHeader>
      <CardContent>
        {logins.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No login history recorded</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="login-history-table">
                <thead>
                  <tr className="border-b border-divine-gold/10">
                    <th className="text-left py-2 px-3 text-gray-400 text-xs uppercase tracking-wider">Timestamp</th>
                    <th className="text-left py-2 px-3 text-gray-400 text-xs uppercase tracking-wider">Partial IP</th>
                    <th className="text-left py-2 px-3 text-gray-400 text-xs uppercase tracking-wider">Country</th>
                    <th className="text-left py-2 px-3 text-gray-400 text-xs uppercase tracking-wider">User ID</th>
                    <th className="text-left py-2 px-3 text-gray-400 text-xs uppercase tracking-wider">User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {pageLogins.map((login, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`login-row-${i}`}>
                      <td className="py-2.5 px-3 text-white/80 whitespace-nowrap text-xs">{formatDate(login.timestamp)}</td>
                      <td className="py-2.5 px-3 font-mono text-white/60 text-xs">{login.partial_ip || "—"}</td>
                      <td className="py-2.5 px-3 text-white/60 text-xs">
                        {login.ip_country ? (
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-divine-gold/60" />{login.ip_country}</span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-white/40 text-[10px]">{login.user_id}</td>
                      <td className="py-2.5 px-3 text-white/30 text-[10px] max-w-[220px] truncate">{login.user_agent || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-divine-gold/10">
                <p className="text-gray-500 text-xs">
                  Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, logins.length)} of {logins.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 h-8 px-2"
                    data-testid="login-page-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 h-8 px-2"
                    data-testid="login-page-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== CHANGE LOG PANEL (admin, in overview) ====================
function ChangeLogPanel({ userId }) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChanges = async () => {
      try {
        const res = await authAxios.get(`/account/admin/change-history/${userId}`);
        setChanges(res.data.changes || []);
      } catch (e) { console.error("Change history fetch error:", e); }
      finally { setLoading(false); }
    };
    fetchChanges();
  }, [userId]);

  const changeIcon = (type) => {
    switch (type) {
      case "Name": return <User className="w-3.5 h-3.5" />;
      case "Email": return <Mail className="w-3.5 h-3.5" />;
      case "Phone": return <Phone className="w-3.5 h-3.5" />;
      case "State": return <MapPin className="w-3.5 h-3.5" />;
      case "Country": return <Globe className="w-3.5 h-3.5" />;
      default: return <Settings className="w-3.5 h-3.5" />;
    }
  };

  return (
    <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="change-log-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" /> Account Change Log
          <span className="text-xs text-gray-500 font-normal ml-2">({changes.length} events)</span>
        </CardTitle>
        <CardDescription className="text-gray-400">
          Audit trail of account detail changes with partial IP
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-divine-gold/30 border-t-divine-gold rounded-full animate-spin" />
          </div>
        ) : changes.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">No account changes recorded</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {changes.map((c, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-divine-black/40 rounded-lg" data-testid={`admin-change-entry-${i}`}>
                <div className="mt-0.5 shrink-0 text-divine-gold">{changeIcon(c.change_type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{c.change_type} updated</span>
                    <span className="text-white/20 text-xs">&bull;</span>
                    <span className="text-white/40 text-xs">{formatDate(c.timestamp)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs">
                    <span className="text-red-400/70 line-through truncate max-w-[160px]">{c.old_value || "empty"}</span>
                    <span className="text-white/20 mx-1">&rarr;</span>
                    <span className="text-green-400/80 truncate max-w-[160px]">{c.new_value || "empty"}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] text-white/25">
                    <span>IP: {c.partial_ip || "—"}</span>
                    {c.ip_country && <span>{c.ip_country}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== MAIN USER PAGE ====================
export default function UserPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [editSponsor, setEditSponsor] = useState(false);
  const [sponsorPick, setSponsorPick] = useState(null); // { user_id, email, name, aff_id } | null
  const [sponsorReason, setSponsorReason] = useState("");
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    notes: "",
    picture: "",
    blocked: false,
    view_only: false,
  });

  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab) => setSearchParams({ tab });

  useEffect(() => {
    fetchUser();
    fetchUserStats();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await authAxios.get(`/admin/users/${userId}`);
      const user = response.data;
      setUserData(user);
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "user",
        notes: user.notes || user.profile?.bio || "",
        picture: user.picture || "",
        blocked: user.blocked || false,
        view_only: user.view_only || false,
      });
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      toast.error("Failed to load user data");
      navigate("/admin");
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await authAxios.get(`/admin/users/${userId}/stats`);
      setUserStats(response.data);
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (formData.name !== userData.name) updates.name = formData.name;
      if (formData.email !== userData.email) updates.email = formData.email;
      if (formData.password) updates.password = formData.password;
      if (formData.role !== userData.role) updates.role = formData.role;
      if (formData.notes !== (userData.notes || userData.profile?.bio || "")) updates.notes = formData.notes;
      if (formData.picture !== (userData.picture || "")) updates.picture = formData.picture;
      if (formData.blocked !== (userData.blocked || false)) updates.blocked = formData.blocked;
      if (formData.view_only !== (userData.view_only || false)) updates.view_only = formData.view_only;

      await authAxios.put(`/admin/users/${userId}`, updates);
      toast.success("User updated successfully");
      setIsEditMode(false);
      fetchUser();
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: userData.name || "",
      email: userData.email || "",
      password: "",
      role: userData.role || "user",
      notes: userData.notes || userData.profile?.bio || "",
      picture: userData.picture || "",
      blocked: userData.blocked || false,
      view_only: userData.view_only || false,
    });
    setIsEditMode(false);
  };

  const handleSaveSponsor = async () => {
    setSavingSponsor(true);
    try {
      // sponsorPick=null means clear the sponsor (orphan). Backend takes an
      // empty string to clear, per admin.py PUT /admin/users/{id}.
      const nextSponsorId = sponsorPick ? sponsorPick.user_id : "";
      const res = await authAxios.put(`/admin/users/${userId}`, {
        sponsor_id: nextSponsorId,
        change_reason: sponsorReason.trim() || "admin_update",
      });
      setUserData(res.data);
      setEditSponsor(false);
      setSponsorPick(null);
      setSponsorReason("");
      toast.success(
        sponsorPick
          ? `Sponsor set to ${sponsorPick.email}`
          : "Sponsor cleared (orphan)"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update sponsor");
    } finally {
      setSavingSponsor(false);
    }
  };

  const handleToggleProfile = async (nextProfile) => {
    if (savingProfile) return;
    const reason = window.prompt(
      `Reason for setting profile to ${nextProfile}? (audited in affiliate_profile_changes)`,
      nextProfile === "PRO" ? "Founding partner / influencer promotion" : "Revert to standard"
    );
    if (reason === null) return;
    setSavingProfile(true);
    try {
      const res = await authAxios.put(`/admin/users/${userId}`, {
        affiliate_compensation_profile: nextProfile,
        change_reason: reason.trim() || `set_${nextProfile.toLowerCase()}`,
      });
      setUserData(res.data);
      toast.success(`Profile set to ${nextProfile}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500/20 text-red-300 border-red-500/30";
      case "support": return "bg-blue-500/20 text-blue-300 border-blue-500/30";
      default: return "bg-divine-gold/20 text-divine-gold border-divine-gold/30";
    }
  };

  const formatCost = (cost) => {
    if (!cost || cost === 0) return "$0.00";
    return `$${cost.toFixed(2)}`;
  };

  const formatTokens = (tokens) => {
    if (!tokens || tokens === 0) return "0";
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(2)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: <User className="h-4 w-4" /> },
    { id: "login-history", label: "Login History", icon: <Clock className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <div className="bg-divine-charcoal border-b border-divine-gold/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="text-divine-gold hover:bg-divine-gold/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                {userData?.picture ? (
                  <img src={userData.picture} alt={userData.name} className="h-12 w-12 rounded-full object-cover border-2 border-divine-gold/30" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-divine-gold/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-divine-gold" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-cinzel text-divine-gold">{userData?.name}</h1>
                  <p className="text-sm text-gray-400">{userData?.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {userData?.blocked && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-red-500/20 text-red-300 border border-red-500/30">
                  <Ban className="h-4 w-4" /> Blocked
                </span>
              )}
              {userData?.view_only && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  <Eye className="h-4 w-4" /> View Only
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize border ${getRoleBadgeColor(userData?.role)}`}>
                {userData?.role}
              </span>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex gap-1 mt-5 border-b border-divine-gold/10 -mb-[1px]">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? "border-divine-gold text-divine-gold"
                    : "border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Guardian status card — always visible at top */}
        <UserGuardianCard userId={userId} />
        {activeTab === "overview" && (
          <>
            {/* Session Stats Panel */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5" /> Session Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-divine-black/50 rounded-lg p-4 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Sessions</p>
                    <p className="text-2xl font-bold text-white">{userStats?.session_count || 0}</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Total Tokens</p>
                    <p className="text-2xl font-bold text-divine-gold">{formatTokens(userStats?.total_tokens)}</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Input</p>
                    <p className="text-xl font-bold text-green-400">{formatTokens(userStats?.input_tokens)}</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Output</p>
                    <p className="text-xl font-bold text-blue-400">{formatTokens(userStats?.output_tokens)}</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Est. Cost</p>
                    <p className="text-xl font-bold text-amber-400">{formatCost(userStats?.estimated_cost)}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <Button
                    onClick={() => navigate(`/plog/${userId}`)}
                    className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    VIEW SESSION LOGS ({userStats?.logged_session_count || 0})
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Token / Coin Metrics Card */}
            <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="token-metrics-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
                  <Coins className="h-5 w-5" /> Token Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div
                    className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10 text-center cursor-pointer hover:border-green-400/40 transition-colors"
                    onClick={() => navigate(`/plog/credits/${userId}`)}
                    data-testid="metric-current-balance"
                  >
                    <Wallet className="h-5 w-5 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{(userData?.coin_balance ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Current Balance</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10 text-center" data-testid="metric-total-earned">
                    <Coins className="h-5 w-5 text-divine-gold mx-auto mb-2" />
                    <p className="text-2xl font-bold text-divine-gold">{(userData?.coin_metrics?.total_earned ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Total Earned</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10 text-center" data-testid="metric-total-spent">
                    <ShoppingCart className="h-5 w-5 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{(userData?.coin_metrics?.total_spent ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Total Spent</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10 text-center" data-testid="metric-total-deducted">
                    <TrendingDown className="h-5 w-5 text-orange-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-orange-400">{(userData?.coin_metrics?.total_deducted ?? 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Total Deducted</p>
                  </div>
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10 text-center" data-testid="metric-transactions">
                    <FileText className="h-5 w-5 text-blue-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-400">{userData?.coin_metrics?.transaction_count ?? 0}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Transactions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Purchase History Card */}
            <AdminPurchaseHistoryPanel userId={userId} />

            {/* Affiliate Info Card */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-divine-gold font-cinzel text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Affiliate Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sponsor */}
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-divine-gold" />
                        <span className="text-sm text-gray-400 uppercase tracking-wider">Sponsor</span>
                      </div>
                      {!editSponsor ? (
                        <button
                          onClick={() => {
                            setEditSponsor(true);
                            // Pre-fill the picker with the current sponsor (if any)
                            // so admins can see who's there and clear/replace intentionally.
                            setSponsorPick(
                              userData?.sponsor_id
                                ? {
                                    user_id: userData.sponsor_id,
                                    email: userData.sponsor_email || "",
                                    name: userData.sponsor_name || "",
                                    aff_id: userData.sponsor_aff_id || "",
                                  }
                                : null
                            );
                          }}
                          className="text-divine-gold/60 hover:text-divine-gold text-xs flex items-center gap-1"
                          data-testid="edit-sponsor-btn"
                        >
                          <Edit className="h-3 w-3" /> Edit
                        </button>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={handleSaveSponsor} disabled={savingSponsor} className="text-green-400 hover:text-green-300 text-xs px-2 py-0.5 rounded bg-green-500/10" data-testid="save-sponsor-btn">
                            {savingSponsor ? "..." : "Save"}
                          </button>
                          <button onClick={() => { setEditSponsor(false); setSponsorPick(null); setSponsorReason(""); }} className="text-gray-400 hover:text-gray-300 text-xs px-2 py-0.5 rounded bg-white/5" data-testid="cancel-sponsor-btn">
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                    {editSponsor ? (
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-500">Sponsor (search by email or name)</Label>
                        <UserEmailPicker
                          value={sponsorPick}
                          onPick={setSponsorPick}
                          excludeUserId={userId}
                          placeholder="Type at least 2 chars…"
                          autoFocus
                          testIdPrefix="sponsor-picker"
                        />
                        <Label className="text-xs text-gray-500">Reason (audit trail)</Label>
                        <Input
                          value={sponsorReason}
                          onChange={(e) => setSponsorReason(e.target.value)}
                          placeholder="e.g. Manual orphan assignment for onboarding cohort"
                          className="bg-divine-black border-divine-gold/30 text-white text-sm"
                          data-testid="sponsor-reason-input"
                        />
                        <p className="text-xs text-gray-500">
                          Clear the picker to remove the sponsor (orphan). Team leader auto-updates from sponsor's sponsor. Writes immutable event to affiliate_assignment_events.
                        </p>
                      </div>
                    ) : userData?.sponsor_id ? (
                      <div>
                        <p className="text-white font-medium">{userData.sponsor_name || "Unknown"}</p>
                        <p className="text-xs text-gray-500 font-mono mt-1">{userData.sponsor_id}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No sponsor assigned (Orphan)</p>
                    )}
                  </div>

                  {/* Team Leader */}
                  <div className="bg-divine-black/50 rounded-lg p-4 border border-divine-gold/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="h-4 w-4 text-divine-gold" />
                      <span className="text-sm text-gray-400 uppercase tracking-wider">Team Leader</span>
                    </div>
                    {userData?.team_leader_id ? (
                      <div>
                        <p className="text-white font-medium">{userData.leader_name || "Unknown"}</p>
                        <p className="text-xs text-gray-500 font-mono mt-1">{userData.team_leader_id}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No team leader assigned</p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">Auto-derived from sponsor&apos;s sponsor</p>
                  </div>
                </div>

                {/* Aff_ID */}
                <div className="mt-4 pt-4 border-t border-divine-gold/10">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Aff_ID:</span>
                    <span className="text-white font-mono">{userData?.aff_id || "—"}</span>
                  </div>
                </div>

                {/* Compensation Profile — STANDARD (20/6/4) vs PRO (30 direct, no upstream) */}
                <div className="mt-4 pt-4 border-t border-divine-gold/10">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white/80 font-medium">Compensation Profile</div>
                      <div className="text-xs text-white/40 mt-0.5">
                        {(userData?.affiliate_compensation_profile || "STANDARD") === "PRO"
                          ? "PRO — Earns 30% on personally referred customers. No upstream commissions on their direct customer transactions."
                          : "STANDARD — Earns 20% on personally referred customers plus normal upstream Sponsor/Leadership commissions."}
                      </div>
                    </div>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${
                        (userData?.affiliate_compensation_profile || "STANDARD") === "PRO"
                          ? "bg-divine-gold/20 text-divine-gold ring-1 ring-divine-gold/40"
                          : "bg-white/5 text-white/60 ring-1 ring-white/10"
                      }`}
                      data-testid="compensation-profile-badge"
                    >
                      {userData?.affiliate_compensation_profile || "STANDARD"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleProfile("STANDARD")}
                      disabled={savingProfile || (userData?.affiliate_compensation_profile || "STANDARD") === "STANDARD"}
                      className="flex-1 text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="profile-standard-btn"
                    >
                      Set STANDARD (20% direct)
                    </button>
                    <button
                      onClick={() => handleToggleProfile("PRO")}
                      disabled={savingProfile || (userData?.affiliate_compensation_profile) === "PRO"}
                      className="flex-1 text-xs px-3 py-1.5 rounded bg-divine-gold/10 hover:bg-divine-gold/20 text-divine-gold ring-1 ring-divine-gold/30 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="profile-pro-btn"
                    >
                      Promote to PRO (30% direct)
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Change Log */}
            <ChangeLogPanel userId={userId} />

            {/* User Information Card */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-divine-gold font-cinzel">User Information</CardTitle>
                  <CardDescription className="text-gray-400">
                    {isEditMode ? "Edit user details and permissions" : "View user details and permissions"}
                  </CardDescription>
                </div>
                {!isEditMode ? (
                  <Button
                    onClick={() => setIsEditMode(true)}
                    className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
                  >
                    <Edit className="h-4 w-4 mr-2" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={handleCancel} variant="outline" className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10">Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-divine-gold text-divine-black hover:bg-divine-gold/90">
                      <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-divine-black/30 rounded-lg border border-divine-gold/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Ban className={`h-5 w-5 ${formData.blocked ? 'text-red-400' : 'text-gray-500'}`} />
                      <div>
                        <Label className="text-gray-200">Block User</Label>
                        <p className="text-xs text-gray-500">Prevents user from logging in</p>
                      </div>
                    </div>
                    <Switch checked={formData.blocked} onCheckedChange={(checked) => setFormData({ ...formData, blocked: checked })} disabled={!isEditMode} className="data-[state=checked]:bg-red-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Eye className={`h-5 w-5 ${formData.view_only ? 'text-blue-400' : 'text-gray-500'}`} />
                      <div>
                        <Label className="text-gray-200">View Only</Label>
                        <p className="text-xs text-gray-500">User can view but not edit or submit</p>
                      </div>
                    </div>
                    <Switch checked={formData.view_only} onCheckedChange={(checked) => setFormData({ ...formData, view_only: checked })} disabled={!isEditMode} className="data-[state=checked]:bg-blue-500" />
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-200">Name</Label>
                  {isEditMode ? (
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-divine-black border-divine-gold/30 text-white" required />
                  ) : (
                    <p className="text-white py-2">{userData?.name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">Email</Label>
                  {isEditMode ? (
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-divine-black border-divine-gold/30 text-white" required />
                  ) : (
                    <p className="text-white py-2">{userData?.email}</p>
                  )}
                </div>

                {/* Password (edit mode only) */}
                {isEditMode && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-200">
                      Password
                      <span className="text-xs text-gray-400 ml-2">(leave empty to keep current)</span>
                    </Label>
                    <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-divine-black border-divine-gold/30 text-white" placeholder="Enter new password" />
                  </div>
                )}

                {/* Role */}
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-gray-200">Role</Label>
                  {isEditMode ? (
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger className="bg-divine-black border-divine-gold/30 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-divine-charcoal border-divine-gold/30">
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="support">Support</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-white py-2 capitalize">{userData?.role}</p>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-gray-200">Notes</Label>
                  {isEditMode ? (
                    <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-divine-black border-divine-gold/30 text-white min-h-[100px]" placeholder="Admin notes about this user..." />
                  ) : (
                    <p className="text-gray-300 py-2 whitespace-pre-wrap">
                      {userData?.notes || userData?.profile?.bio || <span className="text-gray-500 italic">No notes</span>}
                    </p>
                  )}
                </div>

                {/* Picture URL */}
                <div className="space-y-2">
                  <Label htmlFor="picture" className="text-gray-200">Profile Picture URL</Label>
                  {isEditMode ? (
                    <Input id="picture" value={formData.picture} onChange={(e) => setFormData({ ...formData, picture: e.target.value })} className="bg-divine-black border-divine-gold/30 text-white" placeholder="https://..." />
                  ) : (
                    <p className="text-gray-300 py-2 truncate">
                      {userData?.picture || <span className="text-gray-500 italic">No picture</span>}
                    </p>
                  )}
                </div>

                {/* Account Details */}
                <div className="pt-4 border-t border-divine-gold/20">
                  <h3 className="text-sm font-semibold text-gray-400 mb-3">Account Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">User ID</p>
                      <p className="text-gray-300 font-mono text-xs">{userData?.user_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created At</p>
                      <p className="text-gray-300">
                        {new Date(userData?.created_at).toLocaleDateString()} {new Date(userData?.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Language</p>
                      <p className="text-gray-300 capitalize">{userData?.language || "English"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Theme</p>
                      <p className="text-gray-300">{userData?.theme || "Default"}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === "login-history" && (
          <LoginHistoryTab userId={userId} />
        )}
      </div>
    </div>
  );
}
